import React, { useContext, useState } from "react";
import "./BranchAppsUsers.css";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { branchAvailableApps } from "../../../../utils/helpers";
import { integrationLabel, normalizeIntegrationKey } from "../../../../utils/integrations";
import { DeleteModal } from "../../../modals/Delete";
import { translations } from "../../../../utils/lang";
import { Button, Input, Select, StatusDropdown } from "../../../ui";
import { Repeat, Trash2 } from "../../../ui/icons";
import SecretCell from "../../SecretCell";
import { useStoreCredentials } from "../../useStoreCredentials";

const baseApps = { app: "", name: "", id: "", status: "inactive" };
const baseUsers = { app: "", email: "", password: "" };
const restStatus = ["active", "inactive", "closed"];

export default function BranchAppsUsers({
  type,
  data = [],
  onUpdate,
  restaurantId,
  branchId,
}) {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const resource = { restaurantId };

  const [list, setList] = useState(data);
  const [newEntry, setNewEntry] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canUpdate = can("restaurant.branch.appUser.update", resource);
  const canManage = canUpdate;

  // Branch app-user passwords are not part of the restaurant data: revealed one at a time through
  // the credentials endpoint, for roles that may read them.
  const { credentials, loading: secretLoading, failed: secretFailed, reveal, forget } = useStoreCredentials(
    restaurantId,
    type === "userInfo" && canManage,
  );
  const [shownSecrets, setShownSecrets] = useState({});
  const secretKey = (entry, index) => String(entry._id || `new-${index}`);
  const toggleSecret = async (entry, index) => {
    const key = secretKey(entry, index);
    if (shownSecrets[key]) {
      setShownSecrets((prev) => ({ ...prev, [key]: false }));
      return;
    }
    if (entry._id && !(await reveal())) return;
    setShownSecrets((prev) => ({ ...prev, [key]: true }));
  };
  const secretValue = (entry) => {
    if (entry.password) return entry.password; // just typed, not saved yet
    const branch = (credentials?.branches || []).find((item) => String(item._id) === String(branchId));
    return (branch?.userInfo || []).find((item) => String(item._id) === String(entry._id))?.password || "";
  };

  const resetForm = () => {
    setNewEntry(null);
    setEditIndex(null);
  };

  const getAvailableOptions = (currentApp = "") => {
    const selected = list
      .map((item, index) => index !== editIndex && (normalizeIntegrationKey(item.app) || item.app))
      .filter(Boolean);

    return branchAvailableApps.filter(
      (app) => !selected.includes(app) || app === currentApp,
    );
  };

  const handleStatusChange = (status, index) => {
    if (!can("restaurant.branch.appUser.statusUpdate", resource)) return;
    if (type !== "apps") return;

    const updated = [...list];
    updated[index] = { ...updated[index], status };
    setList(updated);
    onUpdate && onUpdate(updated);
  };

  const handleInputChange = (e) => {
    setNewEntry((prev) => ({
      ...prev,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));
  };

  const handleEdit = (index) => {
    setEditIndex(index);
    setNewEntry(type === "apps" ? { ...list[index] } : { ...list[index], password: "" });
  };

  const handleSave = () => {
    if (!can(editIndex === null ? "restaurant.branch.appUser.create" : "restaurant.branch.appUser.update", resource)) return;
    if (type === "apps") {
      if (!newEntry?.app || !newEntry?.name || !newEntry?.id) return;
    } else {
      // A password is required for a NEW account, or when none is stored yet. Editing an
      // existing one without typing it keeps the stored password (the server preserves it).
      const hasStoredPassword = editIndex !== null && Boolean(newEntry?.passwordAvailable);
      if (!newEntry?.app || !newEntry?.email) return;
      if (!newEntry?.password && !hasStoredPassword) return;
    }

    const { passwordAvailable, ...entry } = newEntry;
    if (type !== "apps" && !entry.password) delete entry.password;
    const saved = type === "apps" ? newEntry : { ...entry, ...(passwordAvailable && !entry.password ? { passwordAvailable } : {}) };
    const updated = [...list];

    if (editIndex !== null) {
      updated[editIndex] = saved;
    } else {
      updated.push(saved);
    }

    setList(updated);
    onUpdate(updated);
    forget();
    setShownSecrets({});
    resetForm();
  };

  const handleConfirmedDelete = (index) => {
    if (!can("restaurant.branch.appUser.update", resource)) return;
    const updated = list.filter((_, i) => i !== index);
    setList(updated);
    onUpdate(updated);
    setDeleteTarget(null);

    if (editIndex === index) {
      resetForm();
    }
  };

  const confirmDelete = (index) => {
    setDeleteTarget(index);
  };

  return (
    <div className="branchAppsUsers-container">
      <h4>
        {
          translations[lang].restaurants.restaurantsRow.BranchesDetails
            .branchAppsUsers.title[type]
        }
      </h4>

      <div className="restaurant-subtable-scroll">
        <table className="branchAppsUsers-table-container">
          <thead>
            <tr>
              {translations[
                lang
              ].restaurants.restaurantsRow.BranchesDetails.branchAppsUsers.header[
                type
              ].map((item, i) => {
                if (
                  translations[lang].restaurants.restaurantsRow.BranchesDetails
                    .branchAppsUsers.header[type].length ===
                    i + 1 &&
                  !canUpdate
                )
                  return null;
                return (
                  <th className="branchAppsUsers-table-header" key={i}>
                    {item}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {list.map((entry, i) => (
              <tr key={i}>
                {type === "apps" ? (
                  <>
                    <td className="branchAppsUsers-table-p">{integrationLabel(entry.app, lang)}</td>
                    <td className="branchAppsUsers-table-p">{entry.name}</td>
                    <td className="branchAppsUsers-table-p">{entry.id}</td>
                    <td className="restaurants-list-table-p">
                      <StatusDropdown
                        value={entry.status || "active"}
                        options={restStatus}
                        editable={can("restaurant.branch.appUser.statusUpdate", resource)}
                        onChange={(status) => handleStatusChange(status, i)}
                        getLabel={(status) =>
                          translations[lang].restaurants.restaurantsRow.status[
                            status
                          ]
                        }
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td className="branchAppsUsers-table-p">
                      {translations[lang].appsNames[entry.app] ||
                        translations[lang].notApp + " " + (i + 1)}
                    </td>
                    <td className="branchAppsUsers-table-p">{entry.email}</td>
                    <td className="branchAppsUsers-table-p">
                      <SecretCell
                        available={Boolean(entry.password || entry.passwordAvailable)}
                        value={secretValue(entry)}
                        shown={Boolean(shownSecrets[secretKey(entry, i)])}
                        loading={secretLoading}
                        failed={secretFailed}
                        canReveal={canManage && Boolean(entry._id || entry.password)}
                        onToggle={() => toggleSecret(entry, i)}
                        lang={lang}
                      />
                    </td>
                  </>
                )}

                {canManage && (
                  <td>
                    <div className="branchAppsUsers-actions">
                      <Button
                        className="branchAppsUsers-btn edit"
                        type="button"
                        onClick={() => handleEdit(i)}
                       icon={<Repeat size={16} />}
                        variant="primary"
                        appearance="soft"
                        size="sm"
                        rounded
                      />

                      <Button
                     
                        type="button"
                        onClick={() => confirmDelete(i)}
                        icon={<Trash2 size={18} />}
                        variant="danger"
                        appearance=""
                        size="sm"
                        rounded
                      />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {newEntry ? (
        <div className="branchAppsUsers-add-form">
          {type === "apps" ? (
            <div className="branchAppsUsers-add-form-grid">
              <div>
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.branchAppsUsers.selectApp
                  }
                </p>
                <Select
                  placeholder={lang === "ar" ? "اختر التطبيق" : "Select app"}
                  options={getAvailableOptions(newEntry.app).map((app) => ({
                    value: app,
                    label: translations[lang].appsNames[app] || app,
                  }))}
                  value={newEntry.app}
                  onChange={(e) =>
                    setNewEntry({ ...newEntry, app: e.target.value })
                  }
                />
              </div>

              <div>
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.branchAppsUsers.name
                  }
                </p>
                <Input
                  type="text"
                  name="name"
                  placeholder="name"
                  value={newEntry.name || ""}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.branchAppsUsers.id
                  }
                </p>
                <Input
                  type="text"
                  name="id"
                  placeholder="id"
                  value={newEntry.id || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          ) : (
            <div className="branchAppsUsers-add-form-grid">
              <div>
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.branchAppsUsers.app
                  }
                </p>
                <Input
                  type="text"
                  placeholder="app"
                  value={newEntry.app || ""}
                  onChange={(e) =>
                    setNewEntry({ ...newEntry, app: e.target.value })
                  }
                />
              </div>

              <div>
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.branchAppsUsers.email
                  }
                </p>
                <Input
                  type="email"
                  placeholder="email@email.com"
                  value={newEntry.email || ""}
                  onChange={(e) =>
                    setNewEntry({ ...newEntry, email: e.target.value })
                  }
                />
              </div>

              <div>
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.branchAppsUsers.password
                  }
                </p>
                <Input
                  type="password"
                  placeholder={
                    newEntry.passwordAvailable
                      ? lang === "ar" ? "اتركه فارغًا للإبقاء على الحالية" : "Leave blank to keep the current one"
                      : "password"
                  }
                  autoComplete="new-password"
                  value={newEntry.password || ""}
                  onChange={(e) =>
                    setNewEntry({ ...newEntry, password: e.target.value })
                  }
                />
              </div>
            </div>
          )}

          <div className="branchAppUsers-btns">
            <Button
              type="button"
              variant="primary"
              appearance="filled"
              fullWidth
              onClick={handleSave}
            >
              {editIndex !== null
                ? lang === "ar"
                  ? "حفظ"
                  : "Save"
                : translations[lang].restaurants.restaurantsRow.BranchesDetails
                    .add}
            </Button>

            <Button
              type="button"
              onClick={resetForm}
              variant="danger"
              appearance="soft"
              fullWidth
            >
              {
                translations[lang].restaurants.restaurantsRow.BranchesDetails
                  .cancel
              }
            </Button>
          </div>
        </div>
      ) : (
        can("restaurant.branch.appUser.create", resource) &&
        (type === "apps" ? (
          list.length < branchAvailableApps.length && (
            <Button
              type="button"
              onClick={() => {
                setNewEntry(baseApps);
                setEditIndex(null);
              }}
              variant="primary"
              appearance="soft"
            >
              {
                translations[lang].restaurants.restaurantsRow.BranchesDetails
                  .add
              }
            </Button>
          )
        ) : (
          <Button
            type="button"
            onClick={() => {
              setNewEntry(baseUsers);
              setEditIndex(null);
            }}
            variant="primary"
            appearance="soft"
          >
            {translations[lang].restaurants.restaurantsRow.BranchesDetails.add}
          </Button>
        ))
      )}

      {deleteTarget !== null && (
        <DeleteModal
          id={deleteTarget}
          handleDelete={handleConfirmedDelete}
          cancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
